<script setup>
import axios from 'axios';
import { onMounted, onUnmounted, watch } from 'vue';
import LoadingOverlay from './components/ui/LoadingOverlay.vue';
import MessagesPanel from './components/ui/MessagesPanel.vue';
import CardDetailModal from './components/ui/modals/CardDetailModal.vue';
import { store } from './store';
import { getMessageCount } from './api-service';
import {Notifications} from "@kyvg/vue3-notification";
// Configure Axios
axios.defaults.withCredentials = true;

// Global messaging: the unread badge must be visible from every page, so the
// count lives here at the root and is polled while a seeker is signed in. A new
// message (or an incoming friend request) bumps the badge no matter where they are.
let pollTimer = null;
async function refreshMessageCount() {
  if (!store.session_id) { store.message_count = 0; return; }
  try {
    const d = await getMessageCount(store.session_id);
    store.message_count = (d && d.total) || 0;
  } catch (e) { /* leave the last known count */ }
}
function openMessages() { store.messages_open = true; }
function closeMessages() { store.messages_open = false; refreshMessageCount(); }

onMounted(() => {
  refreshMessageCount();
  pollTimer = setInterval(refreshMessageCount, 45000);
  // Any surface can open messages or ask for a fresh count via window events.
  window.addEventListener('open-messages', openMessages);
  window.addEventListener('messages-refresh', refreshMessageCount);
});
onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
  window.removeEventListener('open-messages', openMessages);
  window.removeEventListener('messages-refresh', refreshMessageCount);
});
// Pull the count the moment a session lands (login / restore).
watch(() => store.session_id, refreshMessageCount);
</script>

<template>
  <div id="app">
    <Notifications position="top center"/>
    <router-view />

    <!-- Global messages overlay — opened from Messages in the account portrait's menu
         (TopRightAccount) via store.messages_open. The standalone button was
         removed in favor of that icon. -->
    <div v-if="store.messages_open" class="global-msg-overlay" @click.self="closeMessages">
      <div class="global-msg-modal">
        <button class="global-msg-close" aria-label="Close" @click="closeMessages"><i class="fas fa-times"></i></button>
        <MessagesPanel />
      </div>
    </div>

    <!-- Global card-detail modal — any reading surface opens it via utils/card-detail. -->
    <CardDetailModal v-if="store.card_detail" :card="store.card_detail" @close="store.card_detail = null" />

    <loading-overlay v-if="store.is_loading"/>
  </div>
</template>

<style>
/* The account portrait (TopRightAccount) holds the top-right corner at every
   width (2026-10-25, Chris: "never overlaps anything"). Its size and place, and
   the clearance anything sharing that corner keeps: start below it
   (--tra-clear-top) or end left of it (--tra-clear-right), 8px off. */
:root {
  --tra-size: 42px;
  --tra-top: max(10px, env(safe-area-inset-top, 0px));
  --tra-right: max(14px, env(safe-area-inset-right, 0px));
  --tra-clear-top: calc(var(--tra-top) + var(--tra-size) + 8px);
  --tra-clear-right: calc(var(--tra-right) + var(--tra-size) + 8px);
}

/* App-wide subtle scrollbars: invisible at rest (transparent track + thumb over
   the dark bg), a faint thumb only while hovering the scroll area. Kills the
   chunky default/purple bars across every panel without per-component whack-a-mole. */
* {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
::-webkit-scrollbar { width: 9px; height: 9px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: transparent; border-radius: 999px; }
*:hover::-webkit-scrollbar-thumb { background: rgba(150, 120, 220, 0.30); }
::-webkit-scrollbar-thumb:hover { background: rgba(150, 120, 220, 0.5); }

/* Global messages overlay (opened from Messages in the account portrait's menu). */
.global-msg-overlay {
  position: fixed; inset: 0; z-index: 1401; display: flex; align-items: center; justify-content: center;
  padding: 20px; background: rgba(10, 0, 21, 0.6);
}
.global-msg-modal {
  position: relative; width: 100%; max-width: 560px; max-height: 86vh; overflow-y: auto;
  background: #160a2b; border: 1px solid rgba(93,59,140,0.5);
  border-radius: 16px; box-shadow: 0 24px 60px -12px rgba(0,0,0,0.6);
  /* top strip reserved for the close X so the panel's own header (New) clears it */
  padding: 48px 20px 20px;
}
.global-msg-close {
  position: absolute; top: 12px; right: 12px; z-index: 2; width: 30px; height: 30px; border-radius: 50%;
  background: rgba(255,255,255,0.06); color: #c3a9ff; border: none; cursor: pointer;
}

body {
  background-color: #000000;
  background-position: top center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: contain;
  min-height: 100vh;
  overflow: hidden;
  margin: 0;
}
h2 {
  margin: 0;
}
.fas.fa-gem {
  color: #50C878;
}
textarea {
  width: calc(100% - 30px);
  padding: 4px;
  margin: 0 0;
  border-radius: 10px;
  border: 1px solid #e1e1e1;
  box-sizing: border-box;
  font-size: 16px;
  resize: vertical;
}
/* iOS Safari zooms the page when a focused input renders below 16px. Keep all
   text-entry controls at >=16px on phones so focusing never triggers zoom. */
@media (max-width: 768px) {
  input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]) {
    font-size: 16px;
  }
}
</style>

